import React, { useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import { Box, useTheme } from '@mui/system'
import { Typography, Link } from '@mui/material'
import { CheckboxField } from '@e3dc-react/shell/Elements/InputFields'
import { Trans } from 'react-i18next'
import referral from '../resources/referral.png'
import { useGenerateReferralCode } from '../hooks/use-generate-referral-code'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { hasReachedReferralLimit } from '../utils'

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    overflow: 'hidden',
    boxShadow: theme.effectStyles.shadowMain,
    backgroundPosition: 'right',
    backgroundImage: `linear-gradient(238deg, rgba(121, 180, 28, 0.00)
      61.1%, rgba(121, 180, 28, 0.10) 73.6%, rgba(121, 180, 28, 0.60) 100%),
       linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), url(${referral})`,
    backgroundSize: 'cover',
    [theme.breakpoints.down('md')]: {
      backgroundPosition: '80% center',
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
    },
    '& .Mui-disabled': {
      color: theme.palette.shades.shade02,
      backgroundColor: theme.palette.shades.shade03,
    },
    '& .MuiFormControlLabel-root': {
      cursor: 'default',
    },
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  button: {
    width: '100%',
  },
}))

/*
 * This component is used to display the referral card.
 */
const ReferralCard: React.FC = () => {
  const [isOptIn, setIsOptIn] = useState<boolean>(false)
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const { classes } = useStyles()

  const { handleGenerateReferralCode, referallsAvailble, isGenerating } = useGenerateReferralCode()
  const referralLimitReached = hasReachedReferralLimit(referallsAvailble)
  const isButtonDisabled = referralLimitReached || !isOptIn

  const handleOptIn = (value: boolean): void => {
    setIsOptIn(value)
  }

  return (
    <Box className={classes.container}>
      <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'} maxWidth={'75%'} gap={2}>
        <Typography variant="h2">
          <Trans i18nKey="referral.referral-card.title" />
        </Typography>
        <Typography variant="subtitle2">
          <Trans i18nKey="referral.referral-card.subtitle" />
        </Typography>
      </Box>
      <CheckboxField
        sx={{ color: 'grey.grey05' }}
        onChange={handleOptIn}
        value={isOptIn}
        label={
          <Typography variant="subtitle2" component="span">
            <Trans
              i18nKey={'referral.referral-card.opt-in-text'}
              components={{ a: <Link href={'/referral-terms-and-conditions'} target="_blank" rel="noreferrer" className={classes.link} /> }}
            />
          </Typography>
        }
      />
      <OptionalTooltipWrapper placement="top" hasTooltip={referralLimitReached} titleKey={'referral.referral-card.disabled-button-tooltip'}>
        <ButtonWithLoading
          className={classes.button}
          label={t('referral.referral-card.button')}
          isLoading={isGenerating}
          variant="contained"
          onClick={handleGenerateReferralCode}
          disabled={isButtonDisabled}
        />
      </OptionalTooltipWrapper>
      <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            padding: theme.spacing(1, 2),
          }}
        >
          <Typography variant="subtitle2">{t('referral.referral-card.explaination')}</Typography>
          <Typography variant="subtitle2" color={'primary.main'}>
            {referallsAvailble}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default ReferralCard
